import React, { useState, useEffect, useRef } from 'react';
import Step3 from './Step3';
import { createPolicyService, getPolicy, updatePolicy } from 'services/policyServices';
import Step1 from './Step1';
import { getCountryOptions } from 'common/SelectOptions/countryOptions';
import { getStageOptions } from 'common/SelectOptions/stageOptions';
import { getSystemOptions } from 'common/SelectOptions/systemOptions';
import Step2 from './Step2';
import PolicyPreview from './Preview';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getMilestoneOptions } from 'common/SelectOptions/milestoneOptions';
import { formatToSelectOptions, getPolicyTypes, getSubNationals, requiresPolicyType, supportsSubNational } from 'services/subNationalService';
import { useSelector } from 'react-redux';

const PolicyForm = ({alert}) => {

  const { policyID } = useParams();
  const isAGRAUser = useSelector((state) => state.Profile?.user?.user_organisation?.organisation?.short_name) == "AGRA";
  const isEditMode = Boolean(policyID);

  // Add ref to track if this is the initial load during edit mode
  const isInitialEditLoad = useRef(isEditMode);

  const [currentStep, setCurrentStep] = useState(1);
  const [policyFile, setPolicyFile] = useState(null);
  const [formData, setFormData] = useState({
    policy_request_body: {
      details: '',
      supports_flagship: false,
      country_id: null,
      is_off_track: false,
      policy_scope: 'National',
      sub_national_id: null,
      name: '',
      is_delayed: false,
      is_complete: false,
      policy_type_id: null,
      agra_strategy_version: null,
      is_agra_supported: false,
      stage_id: null,
    },
    system_ids: [],
    partner_request_bodies: [],
    current_stage_completed_milestones_ids: []
  });

  const [countryOptions, setCountryOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [systemOptions, setSystemOptions] = useState([]);
  const [milestoneOptions, setMilestoneOptions] = useState([]);
  const [subNationalOptions, setSubNationalOptions] = useState([]);
  const [policyTypeOptions, setPolicyTypeOptions] = useState([]);

  const [isLoading, setIsLoading] = useState({
    countryOptions: false,
    stageOptions: false,
    systemOptions: false,
    milestoneOptions: false,
    policy: true,
    policyTypeOptions: false,
    subNationalOptions: false
  });

  const [errors, setErrors] = useState({
    countryOptions: null,
    stageOptions: null,
    systemOptions: null,
    milestoneOptions: null,
    policy: null,
    policyTypeOptions: null,
    subNationalOptions: null 
  });

  useEffect(() => {

    const fetchPolicyTypes = async (countryId) => {
      try {
        setIsLoading(prev=>({...prev, policyTypeOptions: true}));
        setErrors(prev=>({...prev, policyTypeOptions: null}));

        const response = await getPolicyTypes(countryId);
        
        const options = formatToSelectOptions(response.data);
        
        setPolicyTypeOptions(options);
      } catch (err) {
        console.error("Error fetching policy types:", err);
        setErrors(prev=>({...prev, policyTypeOptions: "Failed to load policy type options. Please try again later."}));
      } finally {
        setIsLoading(prev=>({...prev, policyTypeOptions: false}));
      }
    }

    // In edit mode, only reset fields after initial policy data is loaded
    if (isEditMode) {
      // Skip resetting if we haven't fully loaded the policy yet
      if (isLoading.policy) {
        return;
      }
    }

    // Skip the reset if it's the initial load in edit mode
    if (!isInitialEditLoad.current) {
      setFormData(prev => ({
        ...prev,
        partner_request_bodies: [],
        policy_request_body: {
          ...prev.policy_request_body,
          policy_scope: 'National',
          policy_type_id: null,
          sub_national_id: null
        }
      }));
    }

    // After the initial load, set the flag to false so future changes will trigger the reset
    isInitialEditLoad.current = false;

    if(requiresPolicyType(formData.policy_request_body?.country_id)){
      fetchPolicyTypes(formData.policy_request_body?.country_id);
    }else{
      setPolicyTypeOptions([]);
    }

  }, [formData.policy_request_body?.country_id]);

  useEffect(()=>{
    const fetchSubNationals = async (countryId) => {
      try {
        setIsLoading(prev=>({...prev, subNationalOptions: true}));
        setErrors(prev=>({...prev, subNationalOptions: null}));

        const response = await getSubNationals(countryId);
        
        const options = formatToSelectOptions(response.data);
        
        setSubNationalOptions(options);
      } catch (err) {
        console.error("Error fetching sub-nationals:", err);
        setErrors(prev=>({...prev, subNationalOptions: "Failed to load sub-national options. Please try again later."}));
      } finally {
        setIsLoading(prev=>({...prev, subNationalOptions: false}));
      }
    };

    if(supportsSubNational(formData.policy_request_body?.country_id) && formData.policy_request_body?.policy_scope === "Sub-national"){
      fetchSubNationals(formData.policy_request_body?.country_id);
    }else{
      setSubNationalOptions([]);
    }
      
  },[formData.policy_request_body.policy_scope, formData.policy_request_body?.country_id])

  const fetchOptions = async (
    fetchFn,
    setterFn,
    optionType,
    errorTypeText
  ) => {
    try {
      setIsLoading(prev => ({ ...prev, [optionType]: true }));
      setErrors(prev => ({ ...prev, [optionType]: null }));
      const options = await fetchFn();
      setterFn(options);
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        [optionType]: `Failed to load ${errorTypeText}. Please try again later.`
      }));
      console.error(`Error loading ${errorTypeText}:`, error);
    } finally {
      setIsLoading(prev => ({ ...prev, [optionType]: false }));
    }
  };

  // Fetch options on component mount
  useEffect(() => {
    const loadOptions = async () => {
      // Fetch all options in parallel
      await Promise.all([
        fetchOptions(getCountryOptions, setCountryOptions, 'countryOptions', 'countries'),
        fetchOptions(getStageOptions, setStageOptions, 'stageOptions', 'stages'),
        fetchOptions(getSystemOptions, setSystemOptions, 'systemOptions', 'systems'),
      ]);
    };

    loadOptions();

    // If in edit mode, fetch policy data
    if (isEditMode) {
      const fetchPolicy = async () => {
        try {
          setIsLoading(prev => ({ ...prev, policy: true }));
          const data = await getPolicy(policyID);
          const policyData = data?.policy_data;
          
          // Transform policy data to match form structure
          setFormData({
            policy_request_body: { 
              details: policyData?.details,
              supports_flagship: policyData?.supports_flagship,
              country_id: policyData?.country_id,
              is_off_track: policyData?.is_off_track,
              policy_scope: policyData?.policy_scope || 'National',
              sub_national_id: policyData?.sub_national_id,
              name: policyData?.name,
              is_delayed: policyData?.is_delayed,
              is_complete: policyData?.is_complete,
              policy_type_id: policyData?.policy_type_id,
              agra_strategy_version: policyData?.agra_strategy_version,
              is_agra_supported: policyData?.is_agra_supported,
              stage_id: policyData?.stage_id,
            },
            system_ids: policyData?.policy_systems?.map(system => ({ id: system.system_id })),
            partner_request_bodies: policyData?.policy_partners?.map(partner => ({
              id: partner?.partner_id,
              short_name: partner?.partner?.short_name,
              long_name: partner?.partner?.long_name,
              is_technical_expert: partner?.partner?.is_technical_expert
            }))
          });
          
          if (policyData?.policy_file) {
            setPolicyFile({name: policyData?.policy_file});
          }
          
        } catch (error) {
          setErrors(prev => ({
            ...prev,
            policy: 'Failed to load policy data. Please try again later.'
          }));
          console.error('Error loading policy:', error);
        } finally {
          setIsLoading(prev => ({ ...prev, policy: false }));
        }
      }
      fetchPolicy();
    }
  }, []);

  useEffect(() => {
    if(formData.policy_request_body.stage_id){
      fetchOptions(()=>getMilestoneOptions(formData.policy_request_body.stage_id), setMilestoneOptions, 'milestoneOptions', 'milestones');
    }else{
      setMilestoneOptions([]);
    }

  }, [formData.policy_request_body.stage_id]);

  const handleSubmit = async () => {
    try {
      if (isEditMode) {
        await updatePolicy(policyID, formData, policyFile, isAGRAUser);
      } else {
        await createPolicyService(formData, policyFile, isAGRAUser);
      }

      alert(`Policy ${isEditMode ? 'updated' : 'created'}`, true);
      setTimeout(()=>window.open(`/policy-directory`), [1000]);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} policy:`, error);
      alert(`Error ${isEditMode ? 'updating' : 'creating'} policy`, false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      policy_request_body: {
        ...prev.policy_request_body,
        [field]: value
      }
    }));
  };

  if (isEditMode && isLoading.policy) {
    return <div>Loading policy data...</div>;
  }

  if (errors.policy) {
    return <div className="text-red-500">{errors.policy}</div>;
  }

  return (
    <div className='page-content'>
      <div className="max-w-5xl mx-auto">
        {currentStep >= 1 && currentStep <= 3 &&
        <>
          <div className="flex space-x-2 mb-9">
            <div 
              className={`flex-1 text-center py-3 px-4 rounded ${currentStep === 1 ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}
            >
              1. Policy Details
            </div>
            <div 
              className={`flex-1 text-center py-3 px-4 rounded ${currentStep === 2 ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}
            >
              2. More Details
            </div>
            <div 
              className={`flex-1 text-center py-3 px-4 rounded ${currentStep === 3 ? 'bg-green-100 text-green-800' : 'bg-gray-100'}`}
            >
              3. Add Partners
            </div>
          </div>
        </>
        }

        {currentStep === 1 && (
          <Step1
            isEditMode={isEditMode} 
            formData={formData} 
            handleInputChange={handleInputChange} 
            setFormData={setFormData} 
            setCurrentStep={setCurrentStep}
            countryOptions={countryOptions}
            stageOptions={stageOptions}
            systemOptions={systemOptions}
            milestoneOptions={milestoneOptions}
            isLoading={isLoading}
            errors={errors}
            subNationalOptions = {subNationalOptions}
            policyTypeOptions = {policyTypeOptions}
            isAGRAUser={isAGRAUser}
          />
        )}

        {currentStep === 2 && 
          <Step2 
            setPolicyFile={setPolicyFile} 
            handleInputChange={handleInputChange} 
            setCurrentStep={setCurrentStep} 
            formData={formData}
            policyFile={policyFile}
            isEditMode={isEditMode}
          />
        }

        {currentStep === 3 && (
          <Step3 
            formData={formData} 
            setFormData={setFormData} 
            setCurrentStep={setCurrentStep}
          />
        )}

        {currentStep === 4 && (
          <PolicyPreview
            formData={formData} 
            policyFile={policyFile}
            createPolicy={handleSubmit} 
            setCurrentStep={setCurrentStep}
            countryOptions={countryOptions}
            systemOptions={systemOptions}
            milestoneOptions={milestoneOptions}
            isEditMode={isEditMode}
            subNationalOptions={subNationalOptions}
            policyTypeOptions={policyTypeOptions}
            isAGRAUser={isAGRAUser}
          />
        )}
      </div>
    </div>
  );
};

export default PolicyForm;